// Fetch wrapper that triggers an auth-failure callback on 401 / 403 from
// Supabase API calls.
//
// Problem: when a user's Supabase refresh token expires mid-session (e.g.
// they've had the tab open overnight, or the session was revoked server-
// side), every subsequent query fails with 401 and the UI cascades into
// "failed to load" banners. supabase-js tries to refresh the token under
// the hood, but if refresh also fails, it leaves the client in a zombie
// state — session object present, but all requests denied.
//
// Solution: intercept non-auth-endpoint responses. On 401 / 403, fire an
// `onAuthFailure` callback once per "failure window" so the app can call
// `supabase.auth.signOut()` and land cleanly on the login screen instead
// of showing a wall of red.
//
// ADR: docs/adr/0007-auth-failure-interceptor.md
// Related: prof-backlog.md Tier 1 #3

import { createLogger } from './logger'

const log = createLogger('supabase:auth-guard')

// ─── Helpers ─────────────────────────────────────────────────────────────────

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  // `Request` has a `.url` property.
  return input.url
}

/**
 * Auth-endpoint URLs can legitimately return 401 as part of the normal
 * sign-in / refresh flow. We must not trigger a global sign-out on those
 * — supabase-js handles its own auth retries internally.
 *
 * Pattern: `/auth/v1/` is the canonical path prefix for the auth API
 * (`/auth/v1/token`, `/auth/v1/logout`, `/auth/v1/user`, etc.).
 */
export function isSupabaseAuthEndpoint(url: string): boolean {
  return url.includes('/auth/v1/')
}

// ─── Factory ─────────────────────────────────────────────────────────────────

export interface AuthGuardedFetchOptions {
  /**
   * Underlying fetch implementation. Pass `globalThis.fetch` in production;
   * tests inject a mock.
   */
  baseFetch: typeof fetch
  /**
   * Invoked once per failure window when a non-auth-endpoint response
   * comes back 401 or 403. Typical production binding:
   * `() => supabase.auth.signOut()`. Test binding: a vi.fn spy.
   *
   * Rejections are swallowed — the caller of fetch still gets the response
   * it expected, including the 401. The app then re-renders based on
   * `onAuthStateChange` SIGNED_OUT.
   */
  onAuthFailure: (status: 401 | 403, url: string) => Promise<unknown> | unknown
}

/**
 * Build an auth-guarded fetch. Stateful — the returned function maintains
 * an in-flight flag so concurrent 401 responses don't each trigger a
 * separate sign-out. The flag clears when `onAuthFailure` settles (resolves
 * or rejects).
 *
 * Caveat: the flag is per-factory-instance. If you construct two
 * guarded-fetches (don't), they each hold their own flag and can fire
 * twice — that's intentional so test isolation is per-factory.
 */
export function createAuthGuardedFetch(opts: AuthGuardedFetchOptions): typeof fetch {
  const { baseFetch, onAuthFailure } = opts

  // True between observing an auth failure and the onAuthFailure callback
  // settling. Concurrent failures during this window are silently dropped
  // to avoid firing signOut() fifty times.
  let handlingFailure = false

  const guarded = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await baseFetch(input, init)

    // Fast-path: non-auth-failure. Covers ~100% of production traffic.
    if (response.status !== 401 && response.status !== 403) {
      return response
    }

    const url = resolveUrl(input)

    // Auth endpoints handle their own 401 — skip.
    if (isSupabaseAuthEndpoint(url)) {
      return response
    }

    // Already handling a failure — drop this one on the floor. The response
    // still propagates to the caller so their query-error branch runs.
    if (handlingFailure) {
      return response
    }

    handlingFailure = true
    log.error(
      `Supabase ${response.status} on ${sanitiseForLog(url)} — triggering sign-out. ` +
      `Session likely expired; user will land on login screen.`,
    )

    // Fire the callback without blocking the response. Three reasons:
    //   1. Callers shouldn't have to wait for `signOut()` (network round-
    //      trip) before they observe the 401 that triggered it.
    //   2. If the callback deadlocks or hangs, every concurrent fetch would
    //      block indefinitely waiting for `handlingFailure` to clear.
    //   3. Reset is in a `.finally` so a rejection still clears the flag;
    //      a later 401 (e.g. after the user signs back in and the new
    //      session somehow fails too) can trigger again.
    Promise.resolve()
      .then(() => onAuthFailure(response.status as 401 | 403, url))
      .catch((e) => {
        log.error('onAuthFailure threw — sign-out did not complete cleanly', e)
      })
      .finally(() => {
        handlingFailure = false
      })

    return response
  }

  return guarded as typeof fetch
}

// ─── Internal ────────────────────────────────────────────────────────────────

/**
 * Strip query strings from URLs before logging to avoid accidentally
 * dumping tokens / API keys to Sentry extras. Supabase client puts the
 * anon key in `apikey` header, but third-party RPCs could add sensitive
 * params.
 */
function sanitiseForLog(url: string): string {
  const qIdx = url.indexOf('?')
  return qIdx >= 0 ? url.slice(0, qIdx) : url
}
